import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Chip,
  makeStyles
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import CloseIcon from '@material-ui/icons/Close';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { showGlobalToast } from 'src/features/uiSlice';
import { messageToastType_const } from 'src/app/constant/config';
import { getListAgencies } from 'src/features/agencySlice';
import { getListUsers } from 'src/features/userSlice';

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    padding: '16px 24px',
    borderBottom: '1px solid #e0e3ef'
  },
  sectionLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: '#555',
    marginBottom: 4
  }
}));
const flattenAgencies = (agencies, parentName = '') => {
  const result = [];
  (agencies || []).forEach(agency => {
    if (!agency) return;
    result.push({ ...agency, parentName });
    if (agency.children && agency.children.length > 0) {
      result.push(...flattenAgencies(agency.children, agency.name || parentName));
    }
  });
  return result;
};

const PaymentReceiptDialog = ({
  open,
  onClose,
  isEditing,
  receiptData,
  onSave,
  listAgencies = [],
  listUsers = [],
  accountOptions = [],
  agencyDebtMap = {},
  formatMoney = val => `${String(val).replace(/\B(?=(\d{3})+(?!\d))/g, '.')} đ`,
  formatInputMoney = val => String(val).replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
  parseInputMoney = val => Number(String(val).replace(/\D/g, '')) || 0
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const reduxAgencies = useSelector(state => state.agencySlice?.listAgencies || []);
  const reduxUsers = useSelector(state => state.userSlice?.listUsers || []);

  const rawAgencies = listAgencies && listAgencies.length > 0 ? listAgencies : reduxAgencies;
  const rawUsers = listUsers && listUsers.length > 0 ? listUsers : reduxUsers;

  const flatAgencies = useMemo(() => flattenAgencies(rawAgencies), [rawAgencies]);

  const [userOptions, setUserOptions] = useState([]);
  const [tabForm, setTabForm] = useState(0); // 0: Đại lý, 1: Khách lẻ
  const [fetchType, setFetchType] = useState('account');
  const [paymentMethod, setPaymentMethod] = useState('CASH'); // 'CASH' | 'TRANSFER'
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const searchTimeout = useRef(null);
  const isCustomerDisabled = Boolean(
    isEditing ||
    receiptData?.id ||
    (receiptData?.payer_type !== 'AGENCY' && (receiptData?.order_id || receiptData?.order_code))
  );

  const [form, setForm] = useState({
    code: '',
    receipt_date: moment().format('YYYY-MM-DD'),
    payer_type: 'AGENCY',
    payer_id: null,
    payer_name: '',
    fullName: '',
    phone: '',
    company: '',
    account_name: '',
    amount: 0,
    account: 'Tiền mặt',
    order_id: '',
    note: ''
  });

  const selectedAgencyDebtInfo = (tabForm === 0 && form?.payer_id && agencyDebtMap[String(form.payer_id)]) || null;
  const targetOrderDebt = receiptData?.order_debt !== undefined
    ? Number(receiptData.order_debt)
    : (receiptData?.order_id || receiptData?.order_code)
      ? Number(receiptData?.amount || 0)
      : null;

  // Chỉ load trước danh sách đại lý (số lượng ít và có cấu trúc cây cha-con).
  // Với khách lẻ (end-user): Không load full mà tìm kiếm LIKE qua API khi người dùng nhập từ khóa.
  useEffect(() => {
    if (open && tabForm === 0) {
      setSuggestionLoading(true);
      dispatch(getListAgencies({ page: 1, page_size: 1000 })).finally(() =>
        setSuggestionLoading(false)
      );
    }
  }, [open, tabForm, dispatch]);

  useEffect(() => {
    if (open) {
      if (receiptData) {
        const isExplicitEndUser = (receiptData.payer_type || receiptData.customer_type) === 'END_USER';
        const isExplicitAgency = (receiptData.payer_type || receiptData.customer_type) === 'AGENCY';

        const matchedAgency = (!isExplicitEndUser && isExplicitAgency) ? flatAgencies.find(a => {
          return (
            (receiptData.payer_id && String(a.id) === String(receiptData.payer_id)) ||
            (receiptData.agency_id && String(a.id) === String(receiptData.agency_id)) ||
            (a.name && receiptData.payer_name && a.name.trim().toLowerCase() === receiptData.payer_name.trim().toLowerCase())
          );
        }) : (!isExplicitEndUser ? flatAgencies.find(a => (
          (receiptData.payer_id && String(a.id) === String(receiptData.payer_id)) ||
          (a.name && receiptData.payer_name && a.name.trim().toLowerCase() === receiptData.payer_name.trim().toLowerCase())
        )) : null);

        const isAgency = !isExplicitEndUser && (isExplicitAgency || Boolean(matchedAgency));
        const rawAccount = receiptData.account || receiptData.target_account || '';
        const isTransfer = rawAccount && !rawAccount.includes('Tiền mặt');
        setPaymentMethod(isTransfer ? 'TRANSFER' : 'CASH');
        setTabForm(isAgency ? 0 : 1);
        setFetchType('account');
        const orderCodeStr = receiptData.order_code || receiptData.order?.code || '';
        const agIdKey = matchedAgency?.id || receiptData.payer_id || receiptData.agency_id;
        const agDebtInfo = (isAgency && agIdKey && agencyDebtMap[String(agIdKey)]) || null;
        const defaultAgencyAmount = receiptData.amount !== undefined && receiptData.amount > 0
          ? receiptData.amount
          : (agDebtInfo?.debt > 0 ? agDebtInfo.debt : 0);

        setForm({
          id: receiptData.id,
          code: receiptData.code || '',
          receipt_date: receiptData.receipt_date || receiptData.date ? moment(receiptData.receipt_date || receiptData.date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
          payer_type: isAgency ? 'AGENCY' : 'END_USER',
          payer_id: matchedAgency ? matchedAgency.id : (receiptData.payer_id || receiptData.customer_id || null),
          payer_name: matchedAgency ? (matchedAgency.name || matchedAgency.fullname || receiptData.payer_name) : (receiptData.payer_name || receiptData.customer_name || ''),
          fullName: matchedAgency ? (matchedAgency.name || matchedAgency.fullname || receiptData.payer_name) : (receiptData.payer_name || receiptData.customer_name || ''),
          phone: receiptData.customer_phone || receiptData.phone || matchedAgency?.phone || matchedAgency?.leader?.phone || '',
          company: receiptData.address || receiptData.company || receiptData.customer_address || matchedAgency?.address || matchedAgency?.leader?.address || '',
          account_name: matchedAgency?.username || matchedAgency?.leader?.username || '',
          amount: isAgency ? defaultAgencyAmount : (receiptData.amount || 0),
          account: rawAccount || (isTransfer ? '' : 'Tiền mặt'),
          order_id: isAgency ? '' : (receiptData.order_id || receiptData.order?.id || ''),
          note: receiptData.note || (isAgency ? `Thu tiền công nợ Đại lý ${matchedAgency?.name || receiptData.payer_name || ''}` : (orderCodeStr ? `Thu tiền đơn hàng #${orderCodeStr}` : ''))
        });
        const fallbackUser = !isAgency && (receiptData.payer_name || receiptData.customer_name || receiptData.payer_id) ? {
          id: receiptData.payer_id || receiptData.customer_id || null,
          full_name: receiptData.payer_name || receiptData.customer_name || '',
          phone: receiptData.customer_phone || receiptData.phone || '',
          address: receiptData.address || receiptData.company || receiptData.customer_address || '',
          username: receiptData.account_name || ''
        } : null;
        setSelectedUser(matchedAgency || fallbackUser || null);
        if (fallbackUser) {
          setUserOptions([fallbackUser]);
        } else {
          setUserOptions([]);
        }
      } else {
        setTabForm(0);
        setFetchType('account');
        setPaymentMethod('CASH');
        setSelectedUser(null);
        setUserOptions([]);
        setForm({
          code: '',
          receipt_date: moment().format('YYYY-MM-DD'),
          payer_type: 'AGENCY',
          payer_id: null,
          payer_name: '',
          fullName: '',
          phone: '',
          company: '',
          account_name: '',
          amount: 0,
          account: 'Tiền mặt',
          order_id: '',
          note: ''
        });
        setSubmitted(false);
      }
    }
  }, [open, receiptData]);

  useEffect(() => {
    const isExplicitEndUser = (receiptData?.payer_type || receiptData?.customer_type) === 'END_USER';
    if (open && receiptData && !selectedUser && flatAgencies.length > 0 && !isExplicitEndUser && tabForm === 0) {
      const pId = receiptData.payer_id || (receiptData.payer_type === 'AGENCY' ? receiptData.agency_id : null);
      const pName = receiptData.payer_name || (receiptData.payer_type === 'AGENCY' ? receiptData.customer_name : '');
      const matched = flatAgencies.find(a => {
        return (
          (pId && String(a.id) === String(pId)) ||
          (pName && a.name && a.name.trim().toLowerCase() === pName.trim().toLowerCase())
        );
      });
      if (matched) {
        setSelectedUser(matched);
        setForm(prev => ({
          ...prev,
          payer_id: matched.id,
          payer_name: matched.name || prev.payer_name,
          fullName: matched.name || prev.fullName,
          phone: prev.phone || matched.phone || matched.leader?.phone || '',
          company: prev.company || matched.address || matched.leader?.address || '',
          account_name: matched.username || matched.leader?.username || prev.account_name
        }));
      }
    }
  }, [open, receiptData, flatAgencies, selectedUser]);

  const handleAutocompleteSearch = keywords => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (!keywords || keywords.trim().length === 0) {
      if (tabForm === 0) {
        setSuggestionLoading(true);
        dispatch(getListAgencies({ page: 1, page_size: 1000 })).finally(() =>
          setSuggestionLoading(false)
        );
      } else {
        setUserOptions([]);
        setSuggestionLoading(false);
      }
      return;
    }

    if (keywords.trim().length < 2) {
      if (tabForm === 1) setUserOptions([]);
      setSuggestionLoading(false);
      return;
    }

    setSuggestionLoading(true);
    searchTimeout.current = setTimeout(async () => {
      if (tabForm === 0) {
        dispatch(getListAgencies({ keywords: keywords.trim(), page: 1, page_size: 1000 })).finally(
          () => setSuggestionLoading(false)
        );
      } else {
        try {
          const res = await dispatch(
            getListUsers({ keywords: keywords.trim(), page: 1, page_size: 30 })
          ).unwrap();
          const users = res?.payload?.users || res?.payload?.data || res?.users || [];
          setUserOptions(Array.isArray(users) ? users : []);
        } catch (e) {
          console.error('Search users failed:', e);
          setUserOptions([]);
        } finally {
          setSuggestionLoading(false);
        }
      }
    }, 400);
  };

  const cleanUserOptions = useMemo(() => {
    if (tabForm !== 1) return [];
    const agencyNames = new Set((flatAgencies || []).map(a => (a.name || a.fullname || '').trim().toLowerCase()).filter(Boolean));
    const agencyUsernames = new Set((flatAgencies || []).map(a => (a.username || a.leader?.username || '').trim().toLowerCase()).filter(Boolean));
    const agencyIds = new Set((flatAgencies || []).map(a => String(a.id)));

    let baseList = [...userOptions];
    if (selectedUser && !baseList.some(u => String(u.id) === String(selectedUser.id))) {
      baseList = [selectedUser, ...baseList];
    }

    return baseList.filter(u => {
      if (!u) return false;
      const roleTitle = (u.role?.title || u.role_name || u.role || '').toLowerCase();
      if (roleTitle === 'agency' || u.role_id === 4 || u.role?.id === 4) return false;
      if (agencyIds.has(String(u.id)) || agencyIds.has(String(u.agency_id)) || agencyIds.has(String(u.agency?.id))) return false;
      const uName = (u.full_name || u.fullname || '').trim().toLowerCase();
      const uUser = (u.username || '').trim().toLowerCase();
      if (agencyNames.has(uName) || agencyUsernames.has(uUser)) return false;
      return true;
    });
  }, [tabForm, userOptions, selectedUser, flatAgencies]);

  const handleFetchUserInfo = selectedOption => {
    setSelectedUser(selectedOption);
    if (!selectedOption) {
      setForm(prev => ({
        ...prev,
        payer_id: null,
        payer_name: '',
        fullName: '',
        phone: '',
        company: '',
        account_name: ''
      }));
      return;
    }

    if (tabForm === 0) {
      const agency = selectedOption;
      const name = agency.name || agency.fullname || '';
      setForm(prev => ({
        ...prev,
        payer_type: 'AGENCY',
        payer_id: agency.id || agency.agency_id || null,
        payer_name: name,
        fullName: name,
        phone: agency.leader?.phone || agency.phone || '',
        company: agency.leader?.address || agency.address || '',
        account_name: agency.username || agency.leader?.username || agency.account || agency.name || ''
      }));
    } else {
      const user = selectedOption;
      const roleTitle = (user.role?.title || user.role_name || user.role || '').toLowerCase();
      const isAgencyUser = roleTitle === 'agency' || user.role_id === 4 || user.role?.id === 4;
      const matchedAgency = isAgencyUser ? flatAgencies.find(a =>
        String(a.id) === String(user.agency_id || user.agency?.id) ||
        String(a.leader_id) === String(user.id) ||
        a.username === user.username ||
        a.leader?.username === user.username ||
        (a.name && user.full_name && a.name.trim().toLowerCase() === user.full_name.trim().toLowerCase())
      ) : null;

      if (isAgencyUser && matchedAgency) {
        const ag = matchedAgency;
        const agId = ag?.id || user.agency_id || user.agency?.id || user.id;
        const agName = ag?.name || user.agency?.name || user.full_name || user.username || '';
        setTabForm(0);
        setSelectedUser(ag || null);
        setForm(prev => ({
          ...prev,
          payer_type: 'AGENCY',
          payer_id: agId,
          payer_name: agName,
          fullName: agName,
          phone: ag?.leader?.phone || ag?.phone || user.phone || '',
          company: ag?.leader?.address || ag?.address || user.address || '',
          account_name: ag?.username || ag?.leader?.username || user.username || ''
        }));
        dispatch(
          showGlobalToast({
            message: `"${agName}" là Đại lý. Hệ thống đã chuyển sang phiếu thu Đại lý!`,
            type: messageToastType_const.info
          })
        );
        return;
      }

      const name = user.full_name || user.fullname || user.username || '';
      setForm(prev => ({
        ...prev,
        payer_type: 'END_USER',
        payer_id: user.id || user.user_id || null,
        payer_name: name,
        fullName: name,
        phone: user.phone || '',
        company: user.agency?.name || user.address || '',
        account_name: user.username || user.account || ''
      }));
    }
  };

  const handleSubmit = () => {
    setSubmitted(true);
    const payerName = form.payer_name?.trim() || form.fullName?.trim();
    if (!payerName) {
      dispatch(
        showGlobalToast({
          message: 'Vui lòng chọn hoặc nhập tên người trả tiền!',
          type: messageToastType_const.warning
        })
      );
      return;
    }
    if (!form.amount || form.amount <= 0) {
      return;
    }

    const accountVal = paymentMethod === 'CASH' ? 'Tiền mặt' : form.account?.trim();
    if (paymentMethod === 'TRANSFER' && !accountVal) {
      dispatch(
        showGlobalToast({
          message: 'Vui lòng nhập tài khoản nhận / STK!',
          type: messageToastType_const.warning
        })
      );
      return;
    }

    const payload = {
      receipt_date: form.receipt_date,
      payer_name: payerName,
      amount: Number(form.amount),
      account: accountVal || 'Tiền mặt',
      note: form.note || null,
      order_id: (form.payer_type === 'AGENCY' || tabForm === 0) ? null : (form.order_id ? Number(form.order_id) : null)
    };

    if (form.payer_type && form.payer_id) {
      payload.payer_type = form.payer_type;
      payload.payer_id = Number(form.payer_id);
    } else {
      payload.payer_type = null;
      payload.payer_id = null;
    }

    onSave(payload);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" style={{ fontWeight: 700, color: '#1a1f36' }}>
            {isEditing ? `Sửa phiếu thu tiền #${form.code || form.id}` : '+ Lập phiếu thu tiền'}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent style={{ padding: 24 }}>
        {/* ── 2 Tab: Đại lý & Khách lẻ ── */}
        <Tabs
          value={tabForm}
          onChange={(_, v) => {
            if (isCustomerDisabled) return;
            setTabForm(v);
            setSelectedUser(null);
            setUserOptions([]);
            setForm(prev => ({
              ...prev,
              payer_type: v === 0 ? 'AGENCY' : 'END_USER',
              payer_id: null,
              payer_name: '',
              fullName: '',
              phone: '',
              company: '',
              account_name: ''
            }));
            setFetchType(v === 0 ? 'account' : 'phone');
          }}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          style={{ marginBottom: 20, borderBottom: '1px solid #eee' }}
        >
          <Tab
            label="Đại lý"
            disabled={isCustomerDisabled}
            style={{ fontWeight: 600, textTransform: 'none' }}
          />
          <Tab
            label="Khách lẻ"
            disabled={isCustomerDisabled}
            style={{ fontWeight: 600, textTransform: 'none' }}
          />
        </Tabs>

        {/* ── Ô tìm kiếm tự động ── */}
        <Box display="flex" flexDirection="column" style={{ marginBottom: 16 }}>
          <Box display="flex" style={{ gap: 8, marginBottom: 8 }}>
            {tabForm === 0 && (
              <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
                <Select
                  value={fetchType}
                  disabled={isCustomerDisabled}
                  onChange={e => setFetchType(e.target.value)}
                >
                  <MenuItem value="account">Tài khoản</MenuItem>
                  <MenuItem value="phone">Số điện thoại</MenuItem>
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth variant="outlined" size="small">
              <Autocomplete
                fullWidth
                disabled={isCustomerDisabled}
                size="small"
                openOnFocus={tabForm === 0}
                options={tabForm === 0 ? flatAgencies : cleanUserOptions}
                loading={suggestionLoading}
                filterOptions={(options, state) => {
                  if (tabForm === 1) return options; // Tìm kiếm LIKE trực tiếp từ API backend
                  const input = (state.inputValue || '').trim().toLowerCase();
                  if (!input) return options;
                  return options.filter(opt => {
                    if (!opt) return false;
                    const name = (opt.name || opt.fullname || '').toLowerCase();
                    const username = (opt.username || opt.leader?.username || '').toLowerCase();
                    const phone = (opt.leader?.phone || opt.phone || '').toLowerCase();
                    const business = (opt.leader?.business_name || '').toLowerCase();
                    const idStr = String(opt.id || '');
                    return (
                      name.includes(input) ||
                      username.includes(input) ||
                      phone.includes(input) ||
                      business.includes(input) ||
                      idStr.includes(input)
                    );
                  });
                }}
                value={selectedUser}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  const optId = option.id || option.agency_id || option.user_id;
                  const valId = value.id || value.agency_id || value.user_id;
                  return String(optId) === String(valId);
                }}
                getOptionLabel={option => {
                  if (!option) return '';
                  if (typeof option === 'string') return option;
                  if (tabForm === 0) {
                    if (fetchType === 'phone') {
                      return option.leader?.phone || option.phone || option.name || '';
                    }
                    return option.name || option.username || option.leader?.username || `Đại lý #${option.id}`;
                  }
                  return option.full_name || option.fullname || option.name || option.phone || option.username || '';
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') {
                    handleAutocompleteSearch(newInputValue);
                    setForm(prev => ({ ...prev, payer_name: newInputValue, fullName: newInputValue }));
                    if (!newInputValue) setSelectedUser(null);
                  } else if (reason === 'clear') {
                    setSelectedUser(null);
                    setForm(prev => ({
                      ...prev,
                      payer_id: null,
                      payer_name: '',
                      fullName: '',
                      phone: '',
                      company: '',
                      account_name: ''
                    }));
                  }
                }}
                onChange={(event, newValue) => {
                  setSelectedUser(newValue);
                  handleFetchUserInfo(newValue);
                }}
                noOptionsText={
                  suggestionLoading
                    ? 'Đang tìm kiếm...'
                    : tabForm === 1
                      ? 'Nhập tên hoặc SĐT để tìm kiếm...'
                      : 'Không tìm thấy kết quả'
                }
                renderOption={option => {
                  if (tabForm === 0) {
                    const phone = option.leader?.phone || option.phone || '';
                    const username = option.username || option.leader?.username || '';
                    const parent = option.parentName ? ` (Trực thuộc: ${option.parentName})` : '';
                    return (
                      <Box>
                        <Typography style={{ fontWeight: 600, fontSize: 13 }}>
                          {option.name || option.fullname || `Đại lý #${option.id}`}{parent}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {username ? `TK: ${username}` : ''}
                          {username && phone ? ' - ' : ''}
                          {phone ? `SĐT: ${phone}` : ''}
                        </Typography>
                      </Box>
                    );
                  }
                  return (
                    <Box>
                      <Typography style={{ fontWeight: 600, fontSize: 13 }}>
                        {option.full_name || option.fullname || option.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {option.username ? `TK: ${option.username}` : ''}
                        {option.username && option.phone ? ' - ' : ''}
                        {option.phone ? `SĐT: ${option.phone}` : ''}
                      </Typography>
                    </Box>
                  );
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder={
                      isCustomerDisabled
                        ? (receiptData?.order_id || receiptData?.order_code ? 'Khách hàng theo đơn hàng' : 'Đối tượng nộp tiền')
                        : tabForm === 0
                          ? fetchType === 'account'
                            ? 'Tìm tên hoặc tài khoản đại lý...'
                            : 'Tìm số điện thoại đại lý...'
                          : 'Tìm tên hoặc SĐT khách hàng...'
                    }
                    variant="outlined"
                  />
                )}
              />
            </FormControl>
          </Box>
        </Box>

        {/* ── Thẻ hiển thị công nợ Đại lý (gom nhiều đơn) ── */}
        {tabForm === 0 && selectedAgencyDebtInfo && (
          <Box
            p={1.5}
            mb={2}
            style={{
              backgroundColor: '#f5f7fb',
              borderRadius: 8,
              border: '1px solid #d0d7de',
              display: 'flex',
              flexDirection: 'column',
              gap: 6
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 6 }}>
              <Typography style={{ fontSize: 13, fontWeight: 700, color: '#1a1f36' }}>
                Công nợ Đại lý: {form.payer_name}
              </Typography>
              <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                <Chip size="small" label={`Tổng mua: ${formatMoney(selectedAgencyDebtInfo.totalSales || 0)}`} style={{ fontSize: 11, height: 20 }} />
                <Chip size="small" label={`Đã thu: ${formatMoney(selectedAgencyDebtInfo.totalPaid || 0)}`} style={{ fontSize: 11, height: 20, background: '#eafaf1', color: '#27ae60' }} />
                <Chip
                  size="small"
                  label={selectedAgencyDebtInfo.debt > 0 ? `Còn nợ: ${formatMoney(selectedAgencyDebtInfo.debt)}` : 'Đã hết nợ'}
                  style={{
                    fontSize: 11,
                    height: 20,
                    background: selectedAgencyDebtInfo.debt > 0 ? '#fff1f0' : '#eafaf1',
                    color: selectedAgencyDebtInfo.debt > 0 ? '#d32f2f' : '#27ae60',
                    fontWeight: 700
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}

        {/* ── Thông tin đối tượng (Tên, SĐT, Địa chỉ) ── */}
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
              {tabForm === 0 ? 'Tên đại lý/công ty' : 'Tên khách hàng'}
            </Typography>
            <TextField
              fullWidth
              disabled
              value={form.fullName || form.payer_name || ''}
              variant="outlined"
              size="small"
              InputProps={{ notched: false }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
              {tabForm === 0 && fetchType === 'phone' ? 'Tài khoản' : 'Số điện thoại'}
            </Typography>
            <TextField
              fullWidth
              disabled
              value={tabForm === 0 && fetchType === 'phone' ? form.account_name : form.phone}
              variant="outlined"
              size="small"
              InputProps={{ notched: false }}
            />
          </Grid>
        </Grid>

        <Box mb={2}>
          <Typography variant="subtitle2" style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
            Địa chỉ
          </Typography>
          <TextField
            fullWidth
            disabled
            value={form.company || ''}
            variant="outlined"
            size="small"
            InputProps={{ notched: false }}
          />
        </Box>

        {/* ── Thông tin phiếu thu tiền ── */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography className={classes.sectionLabel}>
              Hình thức thanh toán <span style={{ color: 'red' }}>*</span>
            </Typography>
            <FormControl fullWidth variant="outlined" size="small">
              <Select
                value={paymentMethod}
                onChange={e => {
                  const val = e.target.value;
                  setPaymentMethod(val);
                  if (val === 'CASH') {
                    setForm(prev => ({ ...prev, account: 'Tiền mặt' }));
                  } else if (form.account === 'Tiền mặt') {
                    setForm(prev => ({ ...prev, account: '' }));
                  }
                }}
              >
                <MenuItem value="CASH">Tiền mặt</MenuItem>
                <MenuItem value="TRANSFER">Chuyển khoản</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography className={classes.sectionLabel}>
              Ngày tháng thu <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="date"
              variant="outlined"
              value={form.receipt_date}
              onChange={e => {
                const val = e.target.value;
                setForm(prev => ({ ...prev, receipt_date: val }));
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={paymentMethod === 'TRANSFER' ? 6 : 12}>
            <Typography className={classes.sectionLabel}>
              Số tiền <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="0 đ"
              value={formatInputMoney(form.amount)}
              onChange={e => {
                const val = parseInputMoney(e.target.value);
                setForm(prev => ({ ...prev, amount: val }));
              }}
              error={submitted && (!form.amount || form.amount <= 0)}
              helperText={submitted && (!form.amount || form.amount <= 0) ? 'Số tiền phải lớn hơn 0' : ''}
              InputProps={{
                style: { fontWeight: 400, color: '#1a1f36' }
              }}
            />
            {Boolean(tabForm === 1 && targetOrderDebt !== null && targetOrderDebt > 0 && form.amount > targetOrderDebt) && (
              <Box
                mt={1}
                p={1}
                borderRadius={1}
                style={{
                  backgroundColor: '#fffbeb',
                  border: '1px solid #fde68a',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  fontSize: 12,
                  color: '#92400e',
                  lineHeight: 1.4
                }}
              >
                <span style={{ fontSize: 14 }}>⚠️</span>
                <span>
                  Số tiền nhập (<strong>{formatMoney(form.amount)}</strong>) lớn hơn số nợ cần thu của đơn ({formatMoney(targetOrderDebt)}).
                  Hệ thống sẽ ghi nhận <strong>Thu thừa: +{formatMoney(form.amount - targetOrderDebt)}</strong> cho đơn này.
                </span>
              </Box>
            )}
          </Grid>

          {paymentMethod === 'TRANSFER' && (
            <Grid item xs={12} sm={6}>
              <Typography className={classes.sectionLabel}>
                Tài khoản nhận (STK / Ngân hàng) <span style={{ color: 'red' }}>*</span>
              </Typography>
              {accountOptions && accountOptions.length > 0 ? (
                <Autocomplete
                  freeSolo
                  size="small"
                  options={accountOptions.filter(acc => acc && !acc.includes('Tiền mặt'))}
                  value={form.account === 'Tiền mặt' ? '' : form.account}
                  onInputChange={(e, val) => setForm(prev => ({ ...prev, account: val }))}
                  onChange={(e, val) => setForm(prev => ({ ...prev, account: val || '' }))}
                  renderInput={params => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Nhập hoặc chọn STK / Ngân hàng..."
                    />
                  )}
                />
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder="Nhập STK / Ngân hàng (VD: Techcombank 1903...)"
                  value={form.account === 'Tiền mặt' ? '' : form.account}
                  onChange={e => {
                    const val = e.target.value;
                    setForm(prev => ({ ...prev, account: val }));
                  }}
                />
              )}
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography className={classes.sectionLabel}>
              Ghi chú
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              size="small"
              variant="outlined"
              placeholder="VD: Thu nốt số tiền còn lại của các đơn tháng 10..."
              value={form.note}
              onChange={e => {
                const val = e.target.value;
                setForm(prev => ({ ...prev, note: val }));
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px', borderTop: '1px solid #e0e3ef' }}>
        <Button onClick={onClose} variant="outlined" style={{ textTransform: 'none', fontWeight: 600 }}>
          Hủy
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          style={{ textTransform: 'none', fontWeight: 700 }}
        >
          {isEditing ? 'Cập nhật phiếu thu' : 'Hoàn thành phiếu thu'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentReceiptDialog;
